import {
  AutocompleteInputChangeReason,
  debounce,
  Grid,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CustomAutocompleteMulti from "../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import FormTable, {
  DataItem,
} from "../../../common/components/functional/formTable/FormTable";
import { LanguageSkillEdit } from "../../../common/components/functional/languageSkillEdit/LanguageSkillEdit";
import getLocale from "../../../common/helpers/getLocale";
import { AutocompleteType } from "../../../common/types/autoComplete";
import useLanguageSkills from "../../../hooks/useLanguageSkills";
import { ContentKey, EditMode } from "../../talentProfile/TalentDetails";
import { LanguageSkillCode } from "../../talents/talentAPI";
import { fetchCertificateDefinitions } from "../orderRowsDetailedSlice";
import { LanguageSkill, OrderFields } from "../orderTypes";

interface Options {
  certificates: AutocompleteType[];
  trainings: AutocompleteType[];
  languages: LanguageSkill[];
}

const initialSearchTerms = {
  skills: "",
  certificates: "",
  languages: "",
};

type Props = {
  values: OrderFields;
  onOrderTemplateChange: (
    name: string,
    value: string | null | AutocompleteType | AutocompleteType[],
  ) => void;
  onLanguageSkillsChange: (name: string, value: LanguageSkill[]) => void;
  isNewOrder: boolean;
};

const Requirements = ({
  values,
  onOrderTemplateChange,
  isNewOrder,
  onLanguageSkillsChange,
}: Props) => {
  const [fieldOptions, setFieldOptions] = useState<Options>({
    certificates: values.orderTemplate.certificates,
    trainings: values.orderTemplate.trainings,
    languages: values.orderTemplate.languages,
  });
  const [searchTerms, setSearchTerms] = useState(initialSearchTerms);
  const [open, setOpen] = useState("");
  const { t } = useTranslation(["order", "skills", "certificates"]);
  const dispatch = useAppDispatch();

  const { getLanguageSkillsNameByCode } = useLanguageSkills();

  const localized = useAppSelector((state) => state.localized);

  const locale = getLocale();

  const trainingTypes = useAppSelector((state) => state.localized.training);
  const trainingOptions = Object.entries(trainingTypes).map((entry) => ({
    id: entry[0],
    name: entry[1],
  }));

  const [selectedLanguage, setSelectedLanguage] = useState<LanguageSkill>();
  const existingLanguageCodes =
    values.orderTemplate.languages.map((language) => language.languageCode) ||
    [];

  const formattedData: DataItem[] = values.orderTemplate.languages
    .map((language) => ({
      values: {
        language: getLanguageSkillsNameByCode(language.languageCode),
        level: t("languageSkill:" + language.skillLevel),
      },
      id: language.id,
      deletable: true,
    }))
    .sort((a, b) => a.values.language.localeCompare(b.values.language));

  const headers = [
    { text: t("languageSkill:language") },
    { text: t("languageSkill:level") },
  ];
  const addButtonLabel = t("languageSkill:add");

  const onInputChange = debounce(
    (key: string, term: string, reason?: AutocompleteInputChangeReason) => {
      setSearchTerms({ ...searchTerms, [key]: term });

      if (!term) {
        setFieldOptions({ ...fieldOptions, [key]: [] });
      }
    },
    400,
  );

  const handleOpen = (key: string) => setOpen(key);

  // Fetch certificate definitions when the certificates Autocomplete options are opened
  useEffect(() => {
    if (open === "certificates") {
      dispatch(
        fetchCertificateDefinitions({
          ...(searchTerms.certificates && { term: searchTerms.certificates }),
          language: locale.code || "fi",
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          const certificationOptions = action.payload.map((certification) => ({
            id: certification.id,
            name: localized.certificate[certification.id],
          })) as AutocompleteType[];

          // FIXME: can't use as default values when fetching new definitions
          setFieldOptions((fieldOptions) => ({
            ...fieldOptions,
            certificates: certificationOptions,
          }));
        }
      });
    }
  }, [
    open,
    locale.code,
    localized.certificate,
    t,
    searchTerms.certificates,
    dispatch,
  ]);

  const onChange = (
    e: SyntheticEvent<Element, Event>,
    newValue: AutocompleteType[] | null,
    name: string,
  ) => {
    if (newValue) {
      onOrderTemplateChange(name, newValue);
    }
  };

  const [editMode, setEditMode] = useState<EditMode>({
    enabled: false,
    content: null,
    id: "",
  });

  const deleteLanguage = (id: string) => {
    onLanguageSkillsChange(
      "languages",
      values.orderTemplate.languages.filter((language) => language.id !== id),
    );
  };

  const handleOpenEditModal = (content: ContentKey, id: string = "") => {
    setEditMode({ enabled: true, content, id });
    setSelectedLanguage(
      values.orderTemplate.languages.find((language) => language.id === id),
    );
  };

  const handleCloseEditModal = () => {
    setEditMode({ enabled: false, content: null, id: "" });
    setSelectedLanguage(undefined);
  };

  const onChangeLanguageSkill = (languageCode: string, level: string) => {
    const edited = {
      id: languageCode,
      languageCode: languageCode as LanguageSkillCode,
      skillLevel: level,
    };
    const existing = values.orderTemplate.languages.find(
      (language) => language.languageCode === languageCode,
    );
    let editedLanguages = [];
    if (!existing) {
      editedLanguages = [...values.orderTemplate.languages, edited];
    } else {
      editedLanguages = values.orderTemplate.languages.map((language) =>
        language.languageCode === languageCode ? edited : language,
      );
    }
    onLanguageSkillsChange("languages", editedLanguages);
    handleCloseEditModal();
  };

  const selectedCertificateOptions = useMemo(() => {
    return values.orderTemplate.certificates.map((certificate) => ({
      id: certificate.id,
      name: localized.certificate[certificate.id],
    }));
  }, [values.orderTemplate.certificates, localized.certificate]);

  const sortedCertificatesOptions = useMemo(() => {
    return fieldOptions.certificates.sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [fieldOptions.certificates]);

  const selectedTrainingOptions = useMemo(() => {
    return values.orderTemplate.trainings.map((training) => ({
      id: training.id,
      name: localized.training[training.id],
    }));
  }, [values.orderTemplate.trainings, localized.training]);

  const sortedTrainingOptions = useMemo(() => {
    return trainingOptions.sort((a, b) => a.name.localeCompare(b.name));
  }, [trainingOptions]);
  return (
    <>
      <Grid container item xs={4} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">{t("common:cardsAndCerts")}</Typography>
      </Grid>
      <Grid item xs={8}>
        <CustomAutocompleteMulti
          name="certificates"
          defaultValue={selectedCertificateOptions}
          options={sortedCertificatesOptions}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={onChange}
          onInputChange={onInputChange}
          onOpen={handleOpen}
          fullWidth
        />
      </Grid>
      <Grid container item xs={4} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">{t("common:trainings")}</Typography>
      </Grid>
      <Grid item xs={8}>
        <CustomAutocompleteMulti
          name="trainings"
          defaultValue={selectedTrainingOptions}
          options={sortedTrainingOptions}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={onChange}
          onInputChange={onInputChange}
          onOpen={handleOpen}
          fullWidth
        />
      </Grid>
      <Grid container item xs={4} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">
          {t("common:languageSkills")}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <FormTable
          data={formattedData}
          headers={headers}
          handleOpenEditModal={handleOpenEditModal}
          handleDelete={deleteLanguage}
          contentKey="languages"
          addButtonLabel={
            isNewOrder === undefined || isNewOrder ? addButtonLabel : undefined
          }
        />
        <DialogComponent
          open={editMode.enabled}
          onClose={handleCloseEditModal}
          sx={{ maxWidth: "360px" }}
        >
          <LanguageSkillEdit
            selectedLanguage={selectedLanguage}
            disabledLanguageCodes={existingLanguageCodes}
            onSaveChanges={onChangeLanguageSkill}
            onClose={handleCloseEditModal}
          />
        </DialogComponent>
      </Grid>
    </>
  );
};

export default Requirements;
